import ColourSwatchVariantEnum from '@models/enums/ColourSwatchVariantEnum';
import ListingTypeEnum from '@models/enums/ListingTypeEnum';
import SiteEnum from '@models/enums/SiteEnum';
import { IArticleCard } from '@models/IArticleCard';
import {
  ColourSwatchCardBG,
  IColourSwatchCard,
} from '@models/IColourSwatchCard';
import { IImageWithAltText } from '@models/IImage';
import { CardType } from '@models/IListing';
import { IProductCard } from '@models/IProductCard';
import { IProductTile } from '@models/IProductTile';
import ArticleCard from '@molecules/ArticleCard/ArticleCard';
import DecoColourSwatchCard from '@molecules/ColourSwatchCard/ColourSwatchCard';
import ColourSwatchCardWithDrawer from '@molecules/ColourSwatchCardWithDrawer/ColourSwatchCardWithDrawer';
import HotspotCard, {
  IHotspotCardExtended,
} from '@molecules/HotspotCard/HotspotCard';
import ProductCard from '@molecules/ProductCard/ProductCard';
import ProductTile from '@organisms/ProductTile/ProductTile';

interface Props {
  readonly item: CardType;
  readonly type: ListingTypeEnum;
  readonly site: SiteEnum;
  readonly gridSize: number;
  readonly cardVariant: ColourSwatchVariantEnum;
  readonly wishlistDrawerLabels?: any;
}

export function renderCard({
  item,
  type,
  site,
  gridSize,
  cardVariant,
  wishlistDrawerLabels,
}: Props) {
  const ColourSwatchCard =
    site === SiteEnum.B2B ? ColourSwatchCardWithDrawer : DecoColourSwatchCard;
  switch (type) {
    case ListingTypeEnum.Colour: {
      const colourCard = item as unknown as IColourSwatchCard;
      const id =
        site === SiteEnum.B2B ? colourCard.id : `${colourCard.link}.html`;

      return (
        <ColourSwatchCard
          {...colourCard}
          id={id}
          createColourImage={false}
          showLikeButton={gridSize !== 0}
          bgColour={ColourSwatchCardBG.White}
          site={site}
          variant={cardVariant}
          wishlistDrawerLabels={
            wishlistDrawerLabels ?? colourCard.wishlistDrawerLabels
          }
        />
      );
    }
    case ListingTypeEnum.Product: {
      if (site === SiteEnum.B2B) {
        return <ProductTile {...(item as unknown as IProductTile)} />;
      }
      return (
        <ProductCard {...(item as unknown as IProductCard)} detailedView />
      );
    }
    case ListingTypeEnum.Inspiration: {
      return (
        <HotspotCard
          {...(item as unknown as IHotspotCardExtended)}
          aspectRatio={2 / 3}
          hideMobileHotspots
          useNewHotspotButton={false}
        />
      );
    }
    case ListingTypeEnum.Article: {
      const { image, link, ...rest } = item as unknown as IArticleCard & {
        image: IImageWithAltText;
        link: string;
      };
      const articleCard: IArticleCard = {
        ...rest,
        url: link,
        image: { ...image.image, altText: image.alternativeText },
      };

      return (
        <ArticleCard
          {...articleCard}
          background="white"
          portraitImages
          truncateTitle={false}
        />
      );
    }
    default:
      return null;
  }
}

export function stableKey(
  item: CardType,
  type: ListingTypeEnum,
  gridSize: number
) {
  switch (type) {
    case ListingTypeEnum.Colour: {
      const colour = item as IColourSwatchCard;
      return `colour-${colour?.id ?? colour?.link}-${gridSize}`;
    }
    case ListingTypeEnum.Product: {
      const product = item as IProductTile;
      return `product-${product?.id ?? product?.heading}-${gridSize}`;
    }
    case ListingTypeEnum.Inspiration: {
      const inspiration = item as unknown as IHotspotCardExtended;
      return `inspiration-${
        inspiration?.link ?? inspiration?.title
      }-${gridSize}`;
    }
    case ListingTypeEnum.Article: {
      const article = item as IArticleCard;
      return `article-${article?.id ?? article?.title}-${gridSize}`;
    }
    default:
      return `item-${Math.random()}-${gridSize}`;
  }
}
